/**
 * Seating props on the ground (rendering half of core/groundFrame.ts).
 *
 * A rock takes the ground's frame: it stands on the facet's normal (or the footprint's fitted normal)
 * and sinks far enough that its base is under the ground on every side — a fraction of its height,
 * plus what the ground's disagreement under it (`spread`) opens up at its rim. A tree keeps growing
 * along the radial (trees grow up, not out of the slope) but sinks by what its trunk needs on the
 * tilt, and its ground tufts take the ground's frame. Anything that lies flat (a pool, a mat) is
 * seated like a rock.
 *
 * `?seating=0` keeps the old placement (radial, base on the centre point) for an A/B in the game.
 * Nothing here reads or writes game state.
 */

import * as THREE from 'three';
import type { PlanetState, Vec3 } from '../types';
import { groundFrame, groundTilt, groundRelief } from '../core/groundFrame';
import { seatGround } from '../core/propSeat';
import type { GroundDials } from '../core/fineTerrain';
import { ROCK_SINK_FRACTION, TREE_SINK_FRACTION } from '../constants';
import { PAD_OVERHANG } from '../constants/action';   // (209's footing follow-up: the one overhang the sim's stands use too)

/** The fine ground's dials (kept on a seat for its callers). */
export type FineGround = GroundDials;
/**
 * 71.2 (DECISIONS 324, ARCHITECTURE §5.36 (3)): a rock or a tree is drawn exactly where the sim stands it — on the fine
 * ground at its footprint's lowest point, sunk by the ground's material (core/propSeat.ts seatGround, the one seat the walk,
 * the shapes, the crowns and the cover read; its `seatHalf` the sim's footprint) — near and far, never on the far ground's
 * lift (67.50 (3): a rock drawn above its sim shape would be false cover). It stood on the coarse ground, the relief
 * flattened under it (67.50 (1)): the owner's "little depressions". Anything else (a hazard, a pool) stands on the mesh.
 */
function seatRadiusOf(planet: PlanetState, seat: PropSeat, coarse: number): number {
  return seat.seatHalf !== undefined && seat.fine && seat.fine.fineAmplitude > 0 ? seatGround(planet, seat.dir, seat.fine, seat.seatHalf) : coarse;
}

/** How a prop meets the ground. */
export type SeatKind = 'rock' | 'tree' | 'flat';

/** What a seated mesh remembers, so a crater under it can seat it again. */
export interface PropSeat {
  planetIndex: number;
  dir: Vec3;
  kind: SeatKind;
  /** The footprint's half-extent and the prop's height, in world units (the model's box × the group's scale). */
  half: number;
  height: number;
  /** For a tree: the trunk's radius in world units (the part that must meet the ground). */
  trunk?: number;
  /** The fine ground to stand on (person scale); absent or amplitude 0 = the mesh. */
  fine?: FineGround;
  /** 71.2: set for what the sim seats on the fine ground (rocks, flora): its footprint there, world units (core/propSeat.ts rockSeatHalf; 0 a tree's trunk). Absent: the mesh. */
  seatHalf?: number;
}

/** Rocks and flat things sink this fraction of their height before the tilt adds more; trees a little. */
const SINK_FRACTION: Record<SeatKind, number> = { rock: ROCK_SINK_FRACTION, flat: 0.08, tree: TREE_SINK_FRACTION };
/**
 * Never deeper than this fraction of the height: a giant rock across several facets would otherwise bury itself
 * by what its whole rim needs, and a boulder a quarter in the ground reads better than one half gone.
 */
const SINK_MAX_FRACTION = 0.25;

let seatingOn: boolean | null = null;
/** On unless the page says `?seating=0` (an A/B against the old radial placement). */
export function propSeatingOn(): boolean {
  if (seatingOn === null) {
    try { seatingOn = typeof location === 'undefined' || new URLSearchParams(location.search).get('seating') !== '0'; }
    catch { seatingOn = true; }
  }
  return seatingOn;
}

const UP = new THREE.Vector3(0, 1, 0);
const tmpN = new THREE.Vector3(), tmpD = new THREE.Vector3(), tmpQ = new THREE.Quaternion();

/** The model's footprint half-extent (the larger of x and z) and height, in its own units, from its box. */
export function modelExtents(model: THREE.Object3D): { half: number; height: number } {
  const box = new THREE.Box3().setFromObject(model);
  if (box.isEmpty()) return { half: 0, height: 0 };
  const size = box.getSize(new THREE.Vector3());
  return { half: Math.max(size.x, size.z) / 2, height: size.y };
}

/**
 * Place `group` on `planet` at `seat.dir` the way `seat.kind` meets the ground. Position and quaternion
 * are set; scale is left alone. Returns the ground's tilt there (radians), for anyone curious.
 */
export function seatGroup(group: THREE.Object3D, planet: PlanetState, seat: PropSeat): number {
  const frame = groundFrame(planet, seat.dir, seat.kind === 'tree' ? 0 : seat.half);
  const tilt = groundTilt(frame, seat.dir);
  tmpD.set(seat.dir.x, seat.dir.y, seat.dir.z).normalize();
  tmpN.set(frame.normal.x, frame.normal.y, frame.normal.z);
  const up = seat.kind === 'tree' ? tmpD : tmpN;
  // How far down: a fraction of the height, plus what the rim opens up on the ground's own disagreement
  // (a rock across a ridge) or, for a tree, what its trunk's edge needs on the tilt.
  const rim = seat.kind === 'tree' ? (seat.trunk ?? seat.half * 0.1) * Math.tan(Math.min(tilt, 1.2)) : seat.half * Math.tan(Math.min(frame.spread, 1.2));
  const sink = Math.min(seat.height * SINK_FRACTION[seat.kind] + rim, seat.height * SINK_MAX_FRACTION);
  const radius = seatRadiusOf(planet, seat, frame.radius);
  group.position.set(
    planet.position.x + tmpD.x * radius - up.x * sink,
    planet.position.y + tmpD.y * radius - up.y * sink,
    planet.position.z + tmpD.z * radius - up.z * sink,
  );
  group.quaternion.setFromUnitVectors(UP, up);
  return tilt;
}

/**
 * A building's seat: it stands upright (along the radial) on a **pad** — a plinth whose top is level with the
 * highest ground under its footprint and whose foot reaches below the lowest — so no wall has ground poking
 * through it and no corner hangs in the air. Drawn, not dug: the terrain is untouched (a pad as a terrain edit
 * is a later step on the edit log, decision 38).
 */
export interface PlinthSeat {
  planetIndex: number;
  dir: Vec3;
  /** Footprint half-extent and height, world units. */
  half: number;
  height: number;
  /** The group's uniform scale (the plinth is a child, built in the group's units). */
  scale: number;
  color: string;
  plinth: THREE.Mesh;
  /** The fine ground to stand on (person scale); absent or amplitude 0 = the mesh. */
  fine?: FineGround;
  /** 67.52: a seated build's base over the coarse ground at its centre (StructureState.baseLift): it stands there, as the sim stands it. */
  lift?: number;
  /** The structure's yaw (StructureState.yaw): the group turned about its up by it, as core/hits.ts structureFrame turns the sim's frame — one frame, drawn and simulated. Absent: none. */
  yaw?: number;
  /** 209's follow-up: the plinth's width over `half` × 2 (default PAD_OVERHANG); 1 for a walkable top, its plinth its deck's, inside its skirt. */
  overhang?: number;
}

/** The pad's top sits this fraction of the building's height above the highest ground; its foot this far below the lowest. */
const PAD_TOP_MARGIN = 0.02, PAD_FOOT_MARGIN = 0.08;

function padGeometry(seat: Omit<PlinthSeat, 'plinth'>, rise: number, drop: number): THREE.BoxGeometry {
  const s = seat.scale || 1;
  const side = (seat.half * 2 * (seat.overhang ?? PAD_OVERHANG)) / s;
  const h = (rise + drop + seat.height * (PAD_TOP_MARGIN + PAD_FOOT_MARGIN)) / s;
  const g = new THREE.BoxGeometry(side, h, side);
  g.translate(0, -h / 2, 0);   // top face at y = 0, the building's base
  return g;
}

/** Shade a CSS hex colour toward dark: the pad reads as the planet's stone, cut and set. */
function padColor(hex: string): THREE.Color {
  const c = new THREE.Color(hex);
  return c.multiplyScalar(0.78);
}

/**
 * Seat `group` upright on a pad at `dir`. Builds the plinth (a child of the group) the first time and re-measures
 * it after; returns the seat to keep. `half`/`height` are world units; `color` is the planet's rock colour.
 */
/**
 * The quaternion a structure stands in: the upright frame (+y to `dir`, three's shortest rotation, which core/math.ts
 * uprightFrame mirrors) turned about its own up by `yaw` — the same turn core/hits.ts structureFrame gives the sim's frame
 * (its local +x toward −z for a positive yaw), so the model, the plinth, the stand, the hit shape, the outline, the terrace
 * and the room graph are one frame (fix/63.14-house-yaw: a settlement's lot stood turned against its footing without it).
 */
export function seatQuaternion(q: THREE.Quaternion, dir: Vec3, yaw?: number): THREE.Quaternion {
  tmpD.set(dir.x, dir.y, dir.z).normalize();
  q.setFromUnitVectors(UP, tmpD);
  if (yaw) q.multiply(tmpYawQ.setFromAxisAngle(UP, yaw));
  return q;
}
const tmpYawQ = new THREE.Quaternion();

/**
 * 63.16 (7): a group seated where a structure stands — its position and turn exactly as seatOnPlinth seats the structure's
 * own model (the coarse ground at `dir` plus the sim's `lift`, turned by `yaw`), with no plinth of its own: for a look
 * that dresses a structure the renderer already stands on its pad (the motor pool's frames and markings on its compound).
 */
export function seatAt(group: THREE.Object3D, planet: PlanetState, seat: Pick<PlinthSeat, 'dir' | 'half' | 'fine'> & { lift: number; yaw?: number }): void {
  const relief = groundRelief(planet, seat.dir, seat.half);
  tmpD.set(seat.dir.x, seat.dir.y, seat.dir.z).normalize();
  const base = relief.radius + seat.lift;
  group.position.set(planet.position.x + tmpD.x * base, planet.position.y + tmpD.y * base, planet.position.z + tmpD.z * base);
  seatQuaternion(group.quaternion, seat.dir, seat.yaw);
}

export function seatOnPlinth(group: THREE.Object3D, planet: PlanetState, seat: Omit<PlinthSeat, 'plinth'> & { plinth?: THREE.Mesh }): PlinthSeat {
  const relief = groundRelief(planet, seat.dir, seat.half);
  tmpD.set(seat.dir.x, seat.dir.y, seat.dir.z).normalize();
  // (67.52: a seated build at its sim base — the coarse ground plus its lift — its pad reaching below the lowest ground)
  const rise = seat.lift !== undefined ? seat.lift : relief.rise + seat.height * PAD_TOP_MARGIN;
  const base = relief.radius + rise;
  group.position.set(planet.position.x + tmpD.x * base, planet.position.y + tmpD.y * base, planet.position.z + tmpD.z * base);
  seatQuaternion(group.quaternion, seat.dir, seat.yaw);   // (turned by the structure's yaw, as the sim's frame is)
  const geo = padGeometry(seat, rise, relief.drop);
  let plinth = seat.plinth;
  if (plinth) {
    plinth.geometry.dispose();
    plinth.geometry = geo;
  } else {
    plinth = new THREE.Mesh(geo, new THREE.MeshLambertMaterial({ color: padColor(seat.color) }));
    plinth.name = 'plinth';
    group.add(plinth);
  }
  return { ...seat, plinth };
}

/** Seat a building mesh again after the ground under it changed. No-op for one placed the old way. */
export function reseatPlinth(pm: { group: THREE.Object3D; plinth?: PlinthSeat }, planets: PlanetState[]): void {
  if (!pm.plinth) return;
  const planet = planets[pm.plinth.planetIndex];
  if (!planet) return;
  pm.plinth = seatOnPlinth(pm.group, planet, pm.plinth);
}

/**
 * For a radially-oriented tree group: the rotation its ground tufts need, in the group's own frame, to lie
 * on the ground's plane rather than on the plane perpendicular to the trunk.
 */
export function groundRotationWithin(group: THREE.Object3D, planet: PlanetState, dir: Vec3): THREE.Quaternion {
  const frame = groundFrame(planet, dir, 0);
  tmpN.set(frame.normal.x, frame.normal.y, frame.normal.z);
  tmpQ.setFromUnitVectors(UP, tmpN);
  return group.quaternion.clone().invert().multiply(tmpQ);
}
